import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ChatService } from '../services';
import type { Conversation, Message, AcsToken, ChatUser, ChatEventData } from '../types';
import { useChatActions } from '../composables/useChatActions';

import { logger } from '@/common/services/config';
export const useChatStore = defineStore('chat', () => {
  const isMobile = computed(() => window.innerWidth <= 768);
  const isLoading = ref(false);
  const isChatConnected = ref(false);
  const error = ref<string | null>(null);
  const currentUser = ref<ChatUser | null>(null);
  const userAcsToken = ref<AcsToken | null>(null);
  const senderAcsId = ref<string | null>(null);
  const conversations = ref<Conversation[]>([]);
  const selectedConversation = ref<Conversation | null>(null);
  const messages = ref<Message[]>([]);
  const isTypingIndicator = ref(false);
  const isLoadingMessages = ref(false);
  const isCreatingConversation = ref(false);

  // Typing indicator state (réactivité Pinia au lieu de CustomEvent)
  const typingSender = ref<string | null>(null);
  const typingThreadId = ref<string | null>(null);
  let typingTimeout: ReturnType<typeof setTimeout> | null = null;

  const chatActions = useChatActions();

  const isStoreInitialized = ref(false);

  // ────────────────────────────────────────────
  // Setters basiques
  // ────────────────────────────────────────────

  const setIsLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  const setError = (err: string | null) => {
    error.value = err;
  };

  const setUserAcsToken = (token: AcsToken | null) => {
    userAcsToken.value = token;
  };

  const setSenderAcsId = (acsId: string | null) => {
    senderAcsId.value = acsId;
  };

  const setCurrentUser = (user: ChatUser | null) => {
    currentUser.value = user;
  };

  const setConversations = (convs: Conversation[]) => {
    conversations.value = convs;
  };

  const setSelectedConversation = (conversation: Conversation | null) => {
    selectedConversation.value = conversation;
    if (conversation) {
      clearUnreadCount(conversation.id);
    }
    // Reset typing indicator on conversation change
    clearTypingIndicator();
  };

  const setMessages = (msgs: Message[]) => {
    messages.value = [...msgs];
  };

  const setIsTypingIndicator = (enabled: boolean) => {
    isTypingIndicator.value = enabled;
  };

  const setIsLoadingMessages = (loading: boolean) => {
    isLoadingMessages.value = loading;
  };

  // ────────────────────────────────────────────
  // Actions temps réel (appelées par ChatService)
  // ────────────────────────────────────────────

  const onNewMessage = (data: ChatEventData['newMessage']) => {
    const selectedConv = selectedConversation.value;
    const isFromMe = data.senderIdentity === senderAcsId.value;

    // Stop typing indicator on incoming message
    clearTypingIndicator();

    if (selectedConv && data.threadId === selectedConv.id) {
      // Message is for the currently selected conversation
      if (isFromMe) {
        // Replace optimistic local message or append
        const optimisticIdx = messages.value.findIndex(
          (msg) =>
            msg.id.startsWith('local-') && msg.content === data.content && msg.author === 'me'
        );

        const newMsg: Message = {
          id: data.messageId,
          content: data.content,
          createdOn: data.createdOn,
          author: 'me',
          attachments: data.attachments || [],
        };

        if (optimisticIdx !== -1) {
          const updated = [...messages.value];
          updated[optimisticIdx] = newMsg;
          messages.value = updated;
        } else {
          messages.value = [...messages.value, newMsg];
        }
      } else {
        const newMsg: Message = {
          id: data.messageId,
          content: data.content,
          createdOn: data.createdOn,
          author: { name: data.senderName },
          attachments: data.attachments || [],
        };
        messages.value = [...messages.value, newMsg];

        // Mark as read since conversation is open
        const convIdx = conversations.value.findIndex((c) => c.id === selectedConv.id);
        if (convIdx !== -1) {
          conversations.value[convIdx].unreadMessagesCount = 0;
        }
        markMessagesAsRead(selectedConv.id).catch(() => {});
      }
    } else {
      // Message for a different conversation → increment unread
      if (!isFromMe) {
        const convIdx = conversations.value.findIndex((c) => c.id === data.threadId);
        if (convIdx !== -1) {
          conversations.value[convIdx].unreadMessagesCount += 1;
        }
      }
    }

    // Update lastMessage on conversation
    conversations.value = conversations.value.map((conv) => {
      if (conv.id === data.threadId) {
        return {
          ...conv,
          lastMessage: {
            id: data.messageId,
            content: data.content,
            createdOn: data.createdOn,
            author: { name: data.senderName },
          },
        };
      }
      return conv;
    });
  };

  const onNewThread = (data: ChatEventData['newThread']) => {
    const alreadyExists = conversations.value.some((c) => c.id === data.threadId);
    if (!alreadyExists) {
      loadConversations().catch(() => {});
    }
  };

  const onThreadUpdated = (data: ChatEventData['threadUpdated']) => {
    conversations.value = conversations.value.map((conv) => {
      if (conv.id === data.threadId) {
        return { ...conv, title: data.topic };
      }
      return conv;
    });

    if (selectedConversation.value?.id === data.threadId) {
      selectedConversation.value = {
        ...selectedConversation.value,
        title: data.topic,
      };
    }
  };

  const onTypingIndicator = (data: ChatEventData['typingIndicator']) => {
    const isCurrentUser = parseInt(data.senderName) === currentUser.value?.id;
    const isCurrentConversation = selectedConversation.value?.id === data.threadId;

    if (!isCurrentUser && isCurrentConversation) {
      typingSender.value = data.senderName;
      typingThreadId.value = data.threadId;
      isTypingIndicator.value = true;

      if (typingTimeout) clearTimeout(typingTimeout);
      typingTimeout = setTimeout(() => {
        clearTypingIndicator();
      }, 4000);
    }
  };

  const onConversationDeleted = (data: ChatEventData['conversationDeleted']) => {
    conversations.value = conversations.value.filter((conv) => conv.id !== data.threadId);

    if (selectedConversation.value?.id === data.threadId) {
      selectedConversation.value = null;
      messages.value = [];
    }
  };

  const clearTypingIndicator = () => {
    typingSender.value = null;
    typingThreadId.value = null;
    isTypingIndicator.value = false;
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      typingTimeout = null;
    }
  };

  const clearUnreadCount = (conversationId: string) => {
    conversations.value = conversations.value.map((conv) => {
      if (conv.id === conversationId) {
        return { ...conv, unreadMessagesCount: 0 };
      }
      return conv;
    });
  };

  // ────────────────────────────────────────────
  // Actions métier
  // ────────────────────────────────────────────

  const initializeChat = async (user: ChatUser) => {
    if (isChatConnected.value && currentUser.value?.email === user.email) {
      return;
    }

    if (isChatConnected.value) {
      await disconnect();
    }

    try {
      isLoading.value = true;
      error.value = null;

      const acsTokenData = await ChatService.initializeAzureChat(user.email);

      setUserAcsToken(acsTokenData);
      setSenderAcsId(acsTokenData.identity);

      await loadConversations();
      isChatConnected.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to initialize chat';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const loadConversations = async () => {
    isLoading.value = true;
    error.value = null;
    await chatActions.loadConversations();
    isLoading.value = false;
  };

  const createConversation = async (topic: string, participants: ChatUser[]) => {
    isCreatingConversation.value = true;
    error.value = null;
    try {
      const response = await chatActions.createNewConversation(topic, participants);
      return response;
    } finally {
      isCreatingConversation.value = false;
    }
  };

  const deleteConversation = async (conversationId: string) => {
    isLoading.value = true;
    error.value = null;
    await chatActions.deleteConversation(conversationId);
    isLoading.value = false;
  };

  const loadMessages = async (conversationId: string, limit: number = 50) => {
    isLoading.value = true;
    setIsLoadingMessages(true);
    error.value = null;
    await chatActions.loadMessages(conversationId, limit);
    await chatActions.markMessagesAsRead(selectedConversation.value?.id || '');
    isLoading.value = false;
    setIsLoadingMessages(false);
  };

  const markMessagesAsRead = async (conversationId: string) => {
    isLoading.value = true;
    error.value = null;
    await chatActions.markMessagesAsRead(conversationId);
    isLoading.value = false;
  };

  const loadMoreMessages = async () => {
    isLoading.value = true;
    error.value = null;
    await chatActions.loadMoreMessages();
    isLoading.value = false;
  };

  const sendMessage = async (data: {
    text: string;
    attachments: File[];
    replyTo?: string | undefined;
    author?: string | undefined;
  }) => {
    if (!selectedConversation.value) {
      logger.error('No conversation selected');
      return;
    }

    isLoading.value = true;
    error.value = null;

    let tempId: string | undefined;

    try {
      tempId = 'local-' + Date.now();
      const localMessage: Message = {
        id: tempId,
        content: data.text,
        createdOn: new Date().toISOString(),
        author: 'me',
        attachments: data.attachments.map((file, index) => ({
          id: 'local-attach-' + Date.now() + '-' + index,
          name: file.name,
          url: URL.createObjectURL(file),
          contentType: file.type,
        })),
      };

      messages.value.push(localMessage);

      conversations.value = conversations.value.map((conv) => {
        if (conv.id === selectedConversation.value?.id) {
          return {
            ...conv,
            lastMessage: {
              id: tempId!,
              content: data.text,
              createdOn: new Date().toISOString(),
              author: 'me',
            },
          };
        }
        return conv;
      });

      if (data.text.trim()) {
        await chatActions.sendMessage({
          text: data.text,
          attachments: [],
          replyTo: data.replyTo,
        });
      }

      if (data.attachments.length > 0) {
        for (const file of data.attachments) {
          await chatActions.sendAttachment(selectedConversation.value.id, file);
        }
      }
    } catch (err) {
      logger.error('Error sending message:', err);
      error.value = 'Failed to send message';

      if (tempId) {
        const messageIndex = messages.value.findIndex((msg) => msg.id === tempId);
        if (messageIndex !== -1) {
          messages.value.splice(messageIndex, 1);
        }
      }
    } finally {
      isLoading.value = false;
    }
  };

  const sendAttachment = async (conversationId: string, file: File) => {
    isLoading.value = true;
    error.value = null;

    try {
      await chatActions.sendAttachment(conversationId, file);
    } catch (err) {
      logger.error('Error sending attachment:', err);
      error.value = 'Failed to send attachment';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const searchStudents = async (query: string) => {
    try {
      return await ChatService.searchStudents(query);
    } catch (err) {
      logger.error('Error searching students:', err);
      setError("Erreur lors de la recherche d'étudiants");
      return [];
    }
  };

  const disconnect = async () => {
    try {
      await ChatService.cleanup();
    } catch (err) {
      logger.error('[Chat Store] Error during Azure cleanup:', err);
    }

    conversations.value = [];
    userAcsToken.value = null;
    currentUser.value = null;
    isChatConnected.value = false;
    clearTypingIndicator();

    isStoreInitialized.value = false;
  };

  const initializeStore = () => {
    if (isStoreInitialized.value) {
      return;
    }
    isStoreInitialized.value = true;
  };

  return {
    // Core State
    isMobile,
    isLoading,
    isChatConnected,
    error,
    userAcsToken,
    senderAcsId,
    currentUser,
    setCurrentUser,
    initializeChat,
    setUserAcsToken,
    setSenderAcsId,
    disconnect,

    // Conversations
    conversations,
    selectedConversation,
    setSelectedConversation,
    setConversations,
    loadConversations,
    createConversation,
    isCreatingConversation,
    deleteConversation,
    searchStudents,

    // Messages
    messages,
    setMessages,
    loadMessages,
    isLoadingMessages,
    setIsLoadingMessages,
    markMessagesAsRead,
    loadMoreMessages,
    sendMessage,
    sendAttachment,

    // Typing Indicator (reactive)
    isTypingIndicator,
    typingSender,
    typingThreadId,
    setIsTypingIndicator,
    clearTypingIndicator,

    // Real-time actions (called by ChatService)
    onNewMessage,
    onNewThread,
    onThreadUpdated,
    onTypingIndicator,
    onConversationDeleted,

    // System
    initializeStore,
    setIsLoading,
    setError,
  };
});
